<template>
    <AdminLayout>
        <template #header>
            <Breadcrumb :items="items" />
        </template>

        <TabView>
            <TabPanel header="Success">
                <DataTable :value="successPayments" :paginator="true" :rows="10" :loading="loading" stripedRows
                    @row-click="onRowClick" class="cursor-pointer">
                    <Column field="orderId" header="Order ID"></Column>
                    <Column field="userId.username" header="Username"></Column>
                    <Column field="userId.email" header="Email"></Column>
                    <Column field="amount" header="Amount">
                        <template #body="slotProps">
                            <span class="font-bold">{{ slotProps.data.currency }} {{ slotProps.data.amount }}.00</span>
                        </template>
                    </Column>
                    <Column field="paymentMethod" header="Method"></Column>
                    <Column field="created_at" header="Date & Time">
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.created_at) }}
                        </template>
                    </Column>
                    <Column field="status" header="Status">
                        <template #body="slotProps">
                            <Tag :value="slotProps.data.status" severity="success" />
                        </template>
                    </Column>
                </DataTable>
            </TabPanel>

            <TabPanel header="Failed">
                <DataTable :value="failedPayments" :paginator="true" :rows="10" :loading="loading" stripedRows
                    @row-click="onRowClick" class="cursor-pointer">
                    <Column field="orderId" header="Order ID"></Column>
                    <Column field="userId.username" header="Username"></Column>
                    <Column field="userId.email" header="Email"></Column>
                    <Column field="amount" header="Amount">
                        <template #body="slotProps">
                            <span class="font-bold">{{ slotProps.data.currency }} {{ slotProps.data.amount }}.00</span>
                        </template>
                    </Column>
                    <Column field="paymentMethod" header="Method"></Column>
                    <Column field="created_at" header="Date & Time">
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.created_at) }}
                        </template>
                    </Column>
                    <!-- <Column field="statusMessage" header="Reason"></Column> -->
                    <Column field="status" header="Status">
                        <template #body="slotProps">
                            <Tag :value="slotProps.data.status" severity="danger" />
                        </template>
                    </Column>
                </DataTable>
            </TabPanel>
        </TabView>

        <!-- Billing Details Dialog -->
        <Dialog v-model:visible="showDialog" modal header="Billing Details" :style="{ width: '600px' }"
            :draggable="false">
            <div v-if="selectedPayment" class="payment-details">
                <!-- Order Information -->
                <div class="detail-section">
                    <h3 class="section-title">Order Information</h3>
                    <div class="detail-row">
                        <span class="detail-label">Order ID:</span>
                        <span class="detail-value">{{ selectedPayment.orderId }}</span>
                    </div>
                    <!-- <div class="detail-row">
                        <span class="detail-label">PayHere ID:</span>
                        <span class="detail-value">{{ selectedPayment.payhereId || 'N/A' }}</span>
                    </div> -->
                    <div class="detail-row">
                        <span class="detail-label">Merchant ID:</span>
                        <span class="detail-value">{{ selectedPayment.merchantId }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Status:</span>
                        <Tag :value="selectedPayment.status"
                            :severity="selectedPayment.status === 'Success' ? 'success' : 'danger'" />
                    </div>
                    <!-- <div class="detail-row">
                        <span class="detail-label">Status Code:</span>
                        <span class="detail-value">{{ selectedPayment.statusCode }}</span>
                    </div> -->
                    <div class="detail-row">
                        <span class="detail-label">Status Message:</span>
                        <span class="detail-value">{{ selectedPayment.statusMessage }}</span>
                    </div>
                </div>

                <!-- User Information -->
                <div class="detail-section">
                    <h3 class="section-title">User Information</h3>
                    <div class="detail-row">
                        <span class="detail-label">Username:</span>
                        <span class="detail-value">{{ selectedPayment.userId?.username }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Email:</span>
                        <span class="detail-value">{{ selectedPayment.userId?.email }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">User Role:</span>
                        <span class="detail-value">{{ selectedPayment.userRole }}</span>
                    </div>
                </div>

                <!-- Payment Information -->
                <div class="detail-section">
                    <h3 class="section-title">Payment Information</h3>
                    <div class="detail-row">
                        <span class="detail-label">Amount:</span>
                        <span class="detail-value font-bold">
                            {{ selectedPayment.currency }} {{ selectedPayment.amount }}.00
                        </span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Captured Amount:</span>
                        <span class="detail-value">
                            {{ selectedPayment.currency }} {{ selectedPayment.capturedAmount }}.00
                        </span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Payment Method:</span>
                        <span class="detail-value">{{ selectedPayment.paymentMethod }}</span>
                    </div>
                </div>

                <!-- Card Information -->
                <div class="detail-section">
                    <h3 class="section-title">Card Information</h3>
                    <div class="detail-row">
                        <span class="detail-label">Card Number:</span>
                        <span class="detail-value">{{ selectedPayment.cardNo }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Card Holder:</span>
                        <span class="detail-value">{{ selectedPayment.cardHolderName }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Card Expiry:</span>
                        <span class="detail-value">{{ selectedPayment.cardExpiry }}</span>
                    </div>
                </div>

                <!-- Transaction Dates -->
                <div class="detail-section">
                    <h3 class="section-title">Transaction Timeline</h3>
                    <div class="detail-row">
                        <span class="detail-label">Initiated At:</span>
                        <span class="detail-value">{{ formatDate(selectedPayment.created_at) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Completed At:</span>
                        <span class="detail-value">{{ formatDate(selectedPayment.updated_at) }}</span>
                    </div>
                </div>
            </div>

            <template #footer>
                <Button label="Close" @click="showDialog = false" severity="secondary" />
            </template>
        </Dialog>

    </AdminLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import AdminLayout from '@/layout/AdminLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Breadcrumb from '@/components/CustomBreadcrumb.vue';
import payments from '@/api/payments';

const items = ref([{ label: "Payments" }]);
const successPayments = ref([]);
const pendingPayments = ref([]);
const failedPayments = ref([]);
const loading = ref(false);
const showDialog = ref(false);
const selectedPayment = ref(null);

const fetchPayments = async () => {
    loading.value = true;
    try {
        const [resSuccess, resPending, resFailed] = await Promise.all([
            payments.getSuccessPayments(),
            payments.getPendingPayments(),
            payments.getFailedPayments()
        ]);

        successPayments.value = resSuccess.data.data || [];
        pendingPayments.value = resPending.data.data || [];
        failedPayments.value = resFailed.data.data || [];

    } catch (error) {
        console.error('Error fetching payments:', error);
    } finally {
        loading.value = false;
    }
};

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}/${month}/${day} - ${d.toLocaleTimeString('en-US')}`;
};

const onRowClick = (event) => {
    selectedPayment.value = event.data;
    showDialog.value = true;
};

onMounted(() => {
    fetchPayments();
});
</script>

<style scoped>
.cursor-pointer :deep(tbody tr) {
    cursor: pointer;
}

.cursor-pointer :deep(tbody tr:hover) {
    background-color: rgba(0, 0, 0, 0.04);
}

.payment-details {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.detail-section {
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 1rem;
}

.detail-section:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.75rem;
    margin-top: 0;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    gap: 1rem;
}

.detail-label {
    font-weight: 500;
    color: #6b7280;
    min-width: 150px;
}

.detail-value {
    color: #111827;
    text-align: right;
    word-break: break-word;
}

.font-bold {
    font-weight: 700;
}
</style>